import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { useTitle } from "../../../hooks/useTitle";
import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Input from "../../../components/BaseUI/Input";
import Button from "../../../components/BaseUI/Button";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import { FiPackage, FiRefreshCw } from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import { formatDateTimeTrInAppTz } from "../../../utils/appTime";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const formatDateTime = formatDateTimeTrInAppTz;

const PartStockAdditions = () => {
  const params = useParams();
  const partId = params?.id || params?.partId;
  useTitle("Rovion MES - Envanter Girişleri");
  const { getToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [search, setSearch] = useState("");
  const [part, setPart] = useState(null);
  const [total, setTotal] = useState(0);
  const [movements, setMovements] = useState([]);
  const [detailsModal, setDetailsModal] = useState({
    isOpen: false,
    movement: null,
  });

  const addToast = (message, type = "info") => {
    const newToast = { id: Date.now(), message, type };
    setToasts((prev) => [...prev, newToast]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== newToast.id)),
      3000,
    );
  };

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const fetchPart = useCallback(async () => {
    if (!partId) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/parts/${partId}`,
        authHeaders(),
      );
      setPart(res.data?.part || null);
    } catch {
      setPart(null);
    }
  }, [authHeaders, partId]);

  const fetchMovements = useCallback(async () => {
    if (!partId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/stock-movements`, {
        ...authHeaders(),
        params: { partId, type: "addition", limit: 200, skip: 0 },
      });
      setMovements(res.data?.movements || []);
      setTotal(Number(res.data?.total) || 0);
    } catch {
      addToast("Envanter girişleri yüklenemedi", "danger");
      setMovements([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, partId]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchPart(), fetchMovements()]);
      setLoading(false);
    };
    load();
  }, [fetchMovements, fetchPart]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return movements;
    return (movements || []).filter((m) => {
      const user = m?.userId?.name || "";
      const notes = m?.notes || "";
      const additionNotes = m?.additionNotes || "";
      const refType = m?.refType || "";
      const refId = m?.refId ? String(m.refId) : "";
      return (
        user.toLowerCase().includes(q) ||
        notes.toLowerCase().includes(q) ||
        additionNotes.toLowerCase().includes(q) ||
        refType.toLowerCase().includes(q) ||
        refId.toLowerCase().includes(q)
      );
    });
  }, [movements, search]);

  const headers = [
    {
      name: "Tarih",
      dataType: "date",
      id: 0,
      customSorter: (a, b) => {
        const ta = new Date(a?.createdAt).getTime();
        const tb = new Date(b?.createdAt).getTime();
        return (Number.isFinite(ta) ? ta : 0) - (Number.isFinite(tb) ? tb : 0);
      },
    },
    {
      name: "Değişim (Δ)",
      dataType: "number",
      id: 2,
      customSorter: (a, b) =>
        (Number(a?.qtyDelta) || 0) - (Number(b?.qtyDelta) || 0),
    },
    {
      name: "Önceki Stok",
      dataType: "number",
      id: 3,
      customSorter: (a, b) =>
        (Number(a?.beforeQty) || 0) - (Number(b?.beforeQty) || 0),
    },
    {
      name: "Sonraki Stok",
      dataType: "number",
      id: 4,
      customSorter: (a, b) =>
        (Number(a?.afterQty) || 0) - (Number(b?.afterQty) || 0),
    },
    {
      name: "Ekleyen",
      dataType: "text",
      id: 5,
      customSorter: (a, b) =>
        String(a?.userId?.name || "").localeCompare(
          String(b?.userId?.name || ""),
        ),
    },
    { name: "Not", dataType: "text", id: 6, sortable: false },
  ];

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("part-stock-additions", headers);

  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: "Parçalar", url: "/parts" },
    {
      label: part?.name
        ? `${part.name}${part.code ? ` (${part.code})` : ""}`
        : "Parça",
    },
    { label: "Envanter Girişleri" },
  ];

  const title = part?.name
    ? `Envanter Girişleri - ${part.name}${part.code ? ` (${part.code})` : ""}`
    : "Envanter Girişleri";

  return (
    <AppPage
      title={title}
      description="Bu parça için envanter giriş hareketleri"
      breadcrumbs={breadcrumbs}
    >
      <CardContainer>
        {loading ? (
          <div className="loading-container">
            <FiPackage className="spin" size={48} />
            <p>Yükleniyor...</p>
          </div>
        ) : (
          <Table
            columns={headers}
            visibleColumns={visibleColumns}
            tableId="part-stock-additions"
            loading={loading}
            viewOptions={
              <ListViewOptionsPanel
                columns={headers}
                visibleColumns={visibleColumns}
                onToggleColumn={toggleColumnVisibility}
                onResetColumns={resetToDefault}
                showApply={false}
                showReset={false}
              />
            }
          >
            {filtered.map((m) => (
              <Tr key={m._id} id={m._id} data={m}>
                {visibleColumns.includes(0) && (
                  <Td>{formatDateTime(m.createdAt)}</Td>
                )}
                {visibleColumns.includes(2) && (
                  <Td>
                    <span style={{ fontFamily: "monospace" }}>
                      {Number(m.qtyDelta) > 0
                        ? `+${m.qtyDelta}`
                        : (m.qtyDelta ?? "-")}
                    </span>
                  </Td>
                )}
                {visibleColumns.includes(3) && (
                  <Td>
                    <span style={{ fontFamily: "monospace" }}>
                      {m.beforeQty ?? "-"}
                    </span>
                  </Td>
                )}
                {visibleColumns.includes(4) && (
                  <Td>
                    <span style={{ fontFamily: "monospace" }}>
                      {m.afterQty ?? "-"}
                    </span>
                  </Td>
                )}
                {visibleColumns.includes(5) && (
                  <Td>{m?.userId?.name || "-"}</Td>
                )}
                {visibleColumns.includes(7) && (
                  <Td>{m?.additionNotes || "-"}</Td>
                )}
              </Tr>
            ))}
          </Table>
        )}

        {!loading && movements.length > 0 && (
          <div className="small text-muted mt-2">
            Gösterilen: {filtered.length}
            {total > 0 ? ` / Toplam: ${total}` : ""}
          </div>
        )}
      </CardContainer>

      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type}
            onClose={() => setToasts((p) => p.filter((x) => x.id !== t.id))}
          />
        ))}
      </ToastContainer>

      <Modal
        isOpen={detailsModal.isOpen}
        onClose={() => setDetailsModal({ isOpen: false, movement: null })}
        width="large"
      >
        <ModalHeader>Envanter Girişi Detayı</ModalHeader>
        <ModalBody>
          <div className="small text-muted mb-2">
            Tarih: {formatDateTime(detailsModal.movement?.createdAt)}
          </div>
          <div className="d-flex flex-wrap gap-3">
            <div>
              <div className="small text-muted">Miktar (Δ)</div>
              <div style={{ fontFamily: "monospace" }}>
                {Number(detailsModal.movement?.qtyDelta) > 0
                  ? `+${detailsModal.movement?.qtyDelta}`
                  : (detailsModal.movement?.qtyDelta ?? "-")}
              </div>
            </div>
            <div>
              <div className="small text-muted">Önce</div>
              <div style={{ fontFamily: "monospace" }}>
                {detailsModal.movement?.beforeQty ?? "-"}
              </div>
            </div>
            <div>
              <div className="small text-muted">Sonra</div>
              <div style={{ fontFamily: "monospace" }}>
                {detailsModal.movement?.afterQty ?? "-"}
              </div>
            </div>
            <div>
              <div className="small text-muted">Ekleyen</div>
              <div>{detailsModal.movement?.userId?.name || "-"}</div>
            </div>
            <div>
              <div className="small text-muted">Referans</div>
              <div style={{ fontFamily: "monospace" }}>
                {detailsModal.movement?.refType
                  ? `${detailsModal.movement?.refType}${
                      detailsModal.movement?.refId
                        ? `:${String(detailsModal.movement?.refId)}`
                        : ""
                    }`
                  : "-"}
              </div>
            </div>
          </div>
          <div className="mt-3">
            <div className="small text-muted">Not</div>
            <div>{detailsModal.movement?.notes || "-"}</div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            appearance="danger-subtle"
            onClick={() => setDetailsModal({ isOpen: false, movement: null })}
          >
            Kapat
          </Button>
        </ModalFooter>
      </Modal>
    </AppPage>
  );
};

export default withAuth(PartStockAdditions, ["admin", "employee", "user"]);
